import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { GridColDef } from "@mui/x-data-grid-pro";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { checkLabel } from "../models/checkLabels";
import { CheckStatus, FleetCheckRow, REPORTED_CHECKS } from "../models/fleetCheckRow";
import { describeCount } from "../utilities/describeCount";
import { formatOdometerNumber } from "@/pages/live-data/utilities/checklistValues";

const STATUS_COLOURS: Record<CheckStatus, "success" | "warning" | "error" | "default"> = {
  notScanned: "default",
  ok: "success",
  warn: "warning",
  alert: "error",
};

/**
 * The columns of the fleet-wide check.
 *
 * The eleven yes/no columns are generated from `REPORTED_CHECKS` rather than written out: they
 * differ only in which check they show, and the original repeated the same definition eleven
 * times, so a change to how a tick renders had to be made eleven times too.
 */
export function useFleetCheckColumns(): GridColDef<FleetCheckRow>[] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        field: "name",
        headerName: t("vehicleDataCheck.columns.asset", "Asset"),
        minWidth: 200,
        flex: 1.3,
        renderCell: ({ row }) => <AssetCell row={row} />,
      },
      {
        field: "licensePlate",
        headerName: t("liveData.checklist.licensePlate", "License plate"),
        minWidth: 130,
      },
      {
        field: "serial",
        headerName: t("vehicleDataCheck.columns.serial", "Serial number"),
        minWidth: 150,
      },
      { field: "vin", headerName: t("vin", "VIN"), minWidth: 170 },
      {
        field: "vinDecoded",
        headerName: t("vehicleDataCheck.columns.vinDecoded", "Vehicle type (VIN)"),
        minWidth: 180,
        flex: 1,
      },
      {
        field: "lastRecordAt",
        headerName: t("vehicleDataCheck.columns.lastRecordAt", "Last record"),
        minWidth: 160,
        type: "dateTime",
        valueGetter: (value: Date | null) => value ?? undefined,
      },
      odometerColumn("rawOdometer", t("liveData.checklist.rawOdometer", "Raw odometer"), t),
      odometerColumn("odometer", t("liveData.checklist.odometer", "Odometer"), t),
      ...REPORTED_CHECKS.map(
        (check): GridColDef<FleetCheckRow> => ({
          field: check,
          headerName: checkLabel(check, t),
          minWidth: 110,
          type: "boolean",
          valueGetter: (_, row) => row.reported[check],
          renderCell: ({ value }) => <ReportedCell reported={!!value} />,
        }),
      ),
      {
        field: "firmware",
        headerName: t("liveData.checklist.firmware", "Firmware"),
        minWidth: 220,
        flex: 1,
      },
      {
        field: "harness",
        headerName: t("vehicleDataCheck.columns.harness", "Harness"),
        minWidth: 150,
        valueGetter: (_, row) =>
          row.harness ? t(`vehicleDataCheck.harness.${row.harness}`, row.harness) : "",
      },
      {
        field: "fmsStandardSoftwareVersion",
        headerName: t(
          "liveData.checklist.fmsStandardSoftwareVersion",
          "Generic FMS standard software version",
        ),
        minWidth: 220,
      },
      {
        field: "canProtocols",
        headerName: t("vehicleDataCheck.columns.canProtocols", "CAN protocols"),
        minWidth: 220,
        flex: 1,
        valueGetter: (_, row) => describeCount(row.canProtocols, ""),
      },
      {
        field: "ioxDevices",
        headerName: t("liveData.checklist.ioxDevices", "IOX devices"),
        minWidth: 220,
        flex: 1,
        valueGetter: (_, row) => describeCount(row.ioxDevices, ""),
      },
      {
        field: "status",
        headerName: t("vehicleDataCheck.columns.status", "Status"),
        minWidth: 120,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            color={STATUS_COLOURS[row.status]}
            label={t(`vehicleDataCheck.status.${row.status}`, row.status)}
          />
        ),
      },
    ],
    // `t` changes identity when the language does, which is the only thing these depend on.
    [t],
  );
}

function odometerColumn(
  field: "rawOdometer" | "odometer",
  headerName: string,
  t: TFunction,
): GridColDef<FleetCheckRow> {
  return {
    field,
    headerName,
    minWidth: 160,
    type: "number",
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }) => <OdometerCell row={row} field={field} t={t} />,
  };
}

/** The vehicle, with a dot saying whether its telematics unit is talking to Geotab right now. */
function AssetCell({ row }: { row: FleetCheckRow }) {
  const { t } = useTranslation();
  const label =
    row.isCommunicating === undefined
      ? t("vehicleDataCheck.notKnown", "not known")
      : row.isCommunicating
        ? t("vehicleDataCheck.communicating", "Communicating")
        : t("liveData.deviceOffline", "Offline");

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <Tooltip title={label}>
        <FiberManualRecordIcon
          titleAccess={label}
          color={row.isCommunicating ? "success" : "disabled"}
          sx={{ fontSize: 10, flexShrink: 0 }}
        />
      </Tooltip>
      <Typography variant="body2" noWrap>
        {row.name}
      </Typography>
    </Stack>
  );
}

function ReportedCell({ reported }: { reported: boolean }) {
  const { t } = useTranslation();
  const label = reported
    ? t("vehicleDataCheck.reported", "reported")
    : t("liveData.checklist.notFound", "not found");

  return reported ? (
    <CheckCircleOutlineIcon color="success" fontSize="small" titleAccess={label} />
  ) : (
    <HelpOutlineIcon color="disabled" fontSize="small" titleAccess={label} />
  );
}

/**
 * An odometer, marked where the two odometers disagree.
 *
 * The warning sits on both of them, because which of the two is wrong is exactly what is not
 * known — only that they cannot both be right.
 */
function OdometerCell({
  row,
  field,
  t,
}: {
  row: FleetCheckRow;
  field: "rawOdometer" | "odometer";
  t: TFunction;
}) {
  const { i18n } = useTranslation();
  const value = row[field];
  const mismatchLabel = t(
    "vehicleDataCheck.odometerMismatch",
    "The raw odometer and the odometer do not agree",
  );

  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent="flex-end"
      sx={{ width: "100%" }}
    >
      <Typography variant="body2">
        {formatOdometerNumber(value, unitOf(row, field), i18n.language)}
      </Typography>
      {row.odometerMismatch && (
        <Tooltip title={mismatchLabel}>
          <WarningAmberIcon color="warning" fontSize="small" titleAccess={mismatchLabel} />
        </Tooltip>
      )}
    </Stack>
  );
}

/** Each odometer carries its own unit; the profile can cover one diagnostic and not the other. */
function unitOf(row: FleetCheckRow, field: "rawOdometer" | "odometer"): string {
  return field === "odometer" ? row.odometerUnit : row.rawOdometerUnit;
}
